import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";

import { QueryOptionsAlone } from "apollo-angular/types";
import { catchError, concatMap, debounceTime, distinctUntilChanged, filter, map, of, switchMap, take } from "rxjs";

import { AssetsGQL, AssetsQuery, AssetsQueryVariables, AvailabilityGQL, AvailabilityQuery, AvailabilityQueryVariables, ScheduleEventsGQL, ScheduleEventsQuery, ScheduleEventsQueryVariables, ViewDatesGQL, ViewDatesQuery, ViewDatesQueryVariables, ZoneDir } from "../../../generated/graphql.generated";
import { dayjs } from "../../core/dayjs/dayjs";
import { eventTypeInfoMap } from "../../global.constants";
import { timelineBookEventFeature } from "../../job/job-overview/overview-timeline/timeline-book-event/timeline-book-event.feature";
import { selectEventToBook } from "../../job/job-overview/overview-timeline/timeline-book-event/timeline-book-event.selectors";
import { selectJobTimezone, selectJobZone } from "../../job/job-tool.reducer";
import { CreateTransactionDialogActions } from "../../transactions/create-transaction-dialog/create-transaction-dialog.actions";
import { selectJobCollectibles } from "../../transactions/transaction-job.selectors";
import { PermissionService } from "../../services/permission.service";

import { BookingScheduleActions } from "./booking-schedule.actions";
import { bookingScheduleFeature } from "./booking-schedule.feature";
import { loadAssetsTriggers, scheduleReloadTriggers } from "./booking-schedule.reducer";
import { selectDepositOutstandingToBookMinor } from "./booking-schedule.selectors";

export const loadAssets = createEffect(
  (
    store = inject(Store),
    actions$ = inject(Actions),
    assetsGQL = inject(AssetsGQL),
    permissionHandler = inject(PermissionService),
  ) => {
    return actions$.pipe(
      ofType(...loadAssetsTriggers),
      // Pair the trigger with the first permission emission; concatLatestFrom
      // drops the action when watchPermission hasn't emitted yet, which would
      // skip the initial asset load until the next trigger.
      concatMap((action) => permissionHandler.watchPermission('assets.view').pipe(
        take(1),
        map((hasAssetsView) => [ action, hasAssetsView ] as const),
      )),
      filter(([, hasAssetsView]) => hasAssetsView),
      // `switchMap`, not `exhaustMap`: a restriction-toggle arriving while a
      // fetch is active must supersede it, or the asset list stays based on the
      // previous restrictionsDisabled value.
      switchMap(() => of(null).pipe(
        concatLatestFrom(() => [
          store.select(selectEventToBook),
          store.select(timelineBookEventFeature.selectBookEventDialog),
        ]),
        map(([ _, eventToBook, { restrictionsDisabled } ]): {
          vars: AssetsQueryVariables;
          opts: QueryOptionsAlone<AssetsQueryVariables, AssetsQuery>;
        } => ({
          vars: {
            filter: {
              zoneDir: ZoneDir.Any,
              types: restrictionsDisabled ? undefined: eventTypeInfoMap[eventToBook.type]?.assetTypes,
            },
            limit: -1,
          },
          opts: {},
        })),
        switchMap(({ vars, opts }) => assetsGQL.fetch(vars, opts).pipe(
          map((res) => BookingScheduleActions.loadAssetsSuccess({
            assets: res.data.assets.assets,
          }))
        )),
        catchError((error) => of(BookingScheduleActions.loadAssetsError({ error }))),
      )),
    );
  },
  { functional: true, dispatch: true }
);

export const checkRequiresReload = createEffect((
    store = inject(Store),
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
      ofType(...scheduleReloadTriggers),
      debounceTime(200),
      concatLatestFrom(() => store.select(bookingScheduleFeature.selectBookingSchedule)),
      filter(([ _action, { start, end, assets } ]) =>{
        return Boolean(start) && Boolean(end) && assets.length > 0;
      }),
      distinctUntilChanged((prev, curr) => {
        const prevData = prev[1];
        const currData = curr[1];

        const sameStart = prevData.start === currData.start;
        const sameEnd = prevData.end === currData.end;

        const prevAssetIds = prevData.assets.map((a) => a.id).sort().join(',') || '';
        const currAssetIds = currData.assets.map((a) => a.id).sort().join(',') || '';
        const sameAssets = prevAssetIds === currAssetIds;

        return sameStart && sameEnd && sameAssets;
      }),
      map(([ _action, { start, end, assets} ]) => BookingScheduleActions.reloadRequired({
        start,
        end,
        assetIds: assets.map((a) => a.id),
      })),
    );

}, { functional: true, dispatch: true });

export const loadEvents = createEffect(
  (
    actions$ = inject(Actions),
    scheduleEventsGQL = inject(ScheduleEventsGQL),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(BookingScheduleActions.reloadRequired),
      concatLatestFrom(() => store.select(selectJobTimezone)),
      map(([ { start, end, assetIds }, timezone ]): {
        vars: ScheduleEventsQueryVariables;
        opts: QueryOptionsAlone<ScheduleEventsQueryVariables, ScheduleEventsQuery>;
      } => ({
        vars: {
          filter: {
            min: dayjs.tz(start, 'YYYY-MM-DD', timezone).unix(),
            max: dayjs.tz(end, 'YYYY-MM-DD', timezone).unix(),
            getEventsOfStatus: ['booked', 'confirmed', 'completed'],
            usesAssets: assetIds,
          },
          limit: 2000,
        },
        opts: {},
      }),
      ),
      switchMap(({ vars, opts }) => scheduleEventsGQL.fetch(vars, opts).pipe(
        map((res) => {
          const allEvents = res.data?.calendarEvents?.events || [];
          // Hide dispatch-only events (reserve, non-billable, ops-manager)
          const visibleEvents = allEvents.filter((e: any) => !e.isDispatchEvent);
          return BookingScheduleActions.loadEventsSuccess({ events: visibleEvents });
        }),
        catchError((error) => of(BookingScheduleActions.loadEventsError({ error }))),
      )),
    );
  },
  { functional: true, dispatch: true }
);

export const openCreateTransactionDialogForBooking = createEffect((
  $actions = inject(Actions),
  store = inject(Store),
) => {
  return $actions.pipe(
    ofType(BookingScheduleActions.payButtonClicked),
    concatLatestFrom(() => [
      store.select(selectJobCollectibles),
      store.select(selectDepositOutstandingToBookMinor),
    ] as const),
    filter(([ , collectibles ]) => Boolean(collectibles)),
    map(([ _action, collectibles, depositOutstandingMinor ]) => {
      // Requirement: the button collects the deposit the warning beside it
      // names. An event invoice is irrelevant here: this entry point creates a
      // deposit, while Pay Event is the entry point that targets that invoice.
      return CreateTransactionDialogActions.bookingPayButtonClicked({
        jobId: collectibles.jobId,
        customerId: collectibles.customerId,
        currency: collectibles.currency,
        amountMinor: depositOutstandingMinor || undefined,
      });
    }),
  );
}, { functional: true, dispatch: true });

export const loadAvailability = createEffect(
  (
    store = inject(Store),
    actions$ = inject(Actions),
    availabilityGQL = inject(AvailabilityGQL),
  ) => {
    return actions$.pipe(
      ofType(BookingScheduleActions.reloadRequired),
      concatLatestFrom(() => [
        store.select(selectEventToBook),
        store.select(selectJobZone),
      ]),
      map(([ { start, end, assetIds }, eventToBook, jobZone ]): {
        vars: AvailabilityQueryVariables;
        opts: QueryOptionsAlone<AvailabilityQueryVariables, AvailabilityQuery>;
      } => {

        const excludeEvents: string[] = [];

        if (eventToBook?.id) {
          excludeEvents.push(eventToBook.id);
        }

        return {
          vars: {
            startDate: start,
            endDate: end,
            objectIds: assetIds,
            excludeEvents,
            zone: jobZone?.id,
          },
          opts: {},
        };
      }),
      switchMap(({ vars, opts }) => availabilityGQL.fetch(vars, opts).pipe(
        map((res) => BookingScheduleActions.loadAvailabilitySuccess({
          availability: res.data?.availabilities || []
        })),
        catchError((error) => of(BookingScheduleActions.loadAvailabilityError({ error }))),
      )),
    );
  },
  { functional: true, dispatch: true }
);

export const loadViewDates = createEffect(
  (
    store = inject(Store),
    actions$ = inject(Actions),
    viewDatesGQL = inject(ViewDatesGQL),
    permissionHandler = inject(PermissionService),
  ) => {
    return actions$.pipe(
      ofType(BookingScheduleActions.reloadRequired),
      concatLatestFrom(() => store.select(selectJobZone)),
      // Pair the reload with the first permission emission; concatLatestFrom
      // drops the action when watchPermission hasn't emitted yet, which would
      // leave the schedule without view dates until the next reload.
      concatMap(([ action, jobZone ]) => permissionHandler.watchPermission('find.viewDates').pipe(
        take(1),
        map((hasViewDates) => [ action, jobZone, hasViewDates ] as const),
      )),
      filter(([, , hasViewDates]) => hasViewDates),
      map(([ { start, end }, jobZone ]): {
        vars: ViewDatesQueryVariables;
        opts: QueryOptionsAlone<ViewDatesQueryVariables, ViewDatesQuery>;
      } => ({
        vars: {
          startDate: start,
          // FullCalendar's currentEnd is exclusive; subtract 1 day to get the inclusive end date
          endDate: dayjs(end).subtract(1, 'day').format('YYYY-MM-DD'),
          zone: jobZone?.id,
        },
        opts: {},
      })),
      // `switchMap`, not `exhaustMap`: a changed range arriving while a fetch is
      // active must supersede it, or the first response can restore stale date
      // remarks over the newer request.
      switchMap(({ vars, opts }) => viewDatesGQL.fetch(vars, opts).pipe(
        map((res) => BookingScheduleActions.loadViewDatesSuccess({
          viewDates: res.data?.viewDates || [],
        })),
        catchError((error) => of(BookingScheduleActions.loadViewDatesError({ error }))),
      )),
    );
  },
  { functional: true, dispatch: true }
);
